import { useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Lock, Trash2, Loader2, ChevronLeft, ChevronRight, UserIcon, ShieldIcon, LogOutIcon, PencilIcon, EyeOff, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'
import {
	AlertDialogCancel
} from '@/components/ui/alert-dialog'
import { signOut } from 'firebase/auth'
import { auth } from '@/constants'
import { BottomSheet } from '@/components/common/BottomSheet'
import ImageCropper, { ImageCropperRef } from '@/components/ui/image-cropper'
import { getFileNameFromUrl } from '@/utils/imageUrls'

// Email update schema
const emailSchema = z.object({
	email: z.string().email('Please enter a valid email address')
})

// Password update schema
const passwordSchema = z.object({
	newPassword: z.string().min(8, 'Password must be at least 8 characters'),
	confirmPassword: z.string().min(1, 'Please confirm your password')
}).refine((data) => data.newPassword === data.confirmPassword, {
	message: "Passwords don't match",
	path: ['confirmPassword']
})

type EmailFormData = z.infer<typeof emailSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

export default function AccountPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { uid, email, displayName, disconnectWallet, isConnected, profileImageUrl, setUserData } = useUser()

	const [isUpdatingEmail, setIsUpdatingEmail] = useState(false)
	const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)
	const [showEmailModal, setShowEmailModal] = useState(false)
	const [showPasswordModal, setShowPasswordModal] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [deletePassword, setDeletePassword] = useState('')
	const [deletePasswordError, setDeletePasswordError] = useState('')
	const [isUploadingImage, setIsUploadingImage] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [isLoggingOut, setIsLoggingOut] = useState(false)
	const [showPassword, setShowPassword] = useState({
		current: false,
		new: false,
		confirm: false,
	})

	const cropperRef = useRef<ImageCropperRef>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const accountActionItems = [
		{ label: 'Change Email', icon: Mail,  onClick: () => setShowEmailModal(true) },
		{ label: 'Change Password', icon: Lock, onClick: () => setShowPasswordModal(true) },
		{ label: 'Delete Account', icon: Trash2, onClick: () => setShowDeleteModal(true) },
		{ label: 'Terms of Use', icon: UserIcon, onClick: () => navigate('/terms-of-use') },
		{ label: 'Privacy Policy', icon: ShieldIcon, onClick: () => navigate('/privacy-policy') },
		{ label: 'Sign Out', icon: LogOutIcon, onClick: () => setShowConfirmModal(true) }
	]
	// Email form
	const emailForm = useForm<EmailFormData>({
		resolver: zodResolver(emailSchema),
		defaultValues: {
			email: email || ''
		}
	})

	// Password form
	const passwordForm = useForm<PasswordFormData>({
		resolver: zodResolver(passwordSchema),
		defaultValues: {
			newPassword: '',
			confirmPassword: ''
		}
	})

	// Handle email update
	const onEmailSubmit = async (data: EmailFormData) => {
		if (data.email === email) {
			toast({
				title: 'No Changes',
				description: 'The email address is the same as your current one.',
				variant: 'destructive'
			})
			return
		}

		setIsUpdatingEmail(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'PATCH',
				body: JSON.stringify({ email: data.email }),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to update email')
			}

			toast({
				title: 'Email Updated',
				description: 'A verification email has been sent to your new address.'
			})
		} catch (error) {
			toast({
				title: 'Update Failed',
				description: error instanceof Error ? error.message : 'Failed to update email',
				variant: 'destructive'
			})
		} finally {
			setIsUpdatingEmail(false)
		}
	}

	// Handle password update
	const onPasswordSubmit = async (data: PasswordFormData) => {
		setIsUpdatingPassword(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'PATCH',
				body: JSON.stringify({
					password: data.newPassword
				}),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to update password')
			}

			toast({
				title: 'Password Updated',
				description: 'Your password has been successfully changed.'
			})
			passwordForm.reset()
		} catch (error) {
			toast({
				title: 'Update Failed',
				description: error instanceof Error ? error.message : 'Failed to update password',
				variant: 'destructive'
			})
		} finally {
			setIsUpdatingPassword(false)
		}
	}

	// Handle profile image upload
	const handleImageUpload = async (file: File) => {
		if (!file) return

		// Validate file type
		if (!file.type.startsWith('image/')) {
			toast({
				title: 'Invalid File',
				description: 'Please select an image file.',
				variant: 'destructive'
			})
			return
		}

		// Validate file size (max 5MB)
		if (file.size > 5 * 1024 * 1024) {
			toast({
				title: 'File Too Large',
				description: 'Please select an image smaller than 5MB.',
				variant: 'destructive'
			})
			return
		}
		const formData = new FormData();
		formData.append("image", file);

		setIsUploadingImage(true)
		try {
			const res = await apiFetch(
			`/v1/me/upload-avatar`,
			{
				method: "POST",
				body: formData,
				withCredentials: true,
				mulipartHeaderTypeSet: true,
			}
			);
		
			const data = await res.json();
			if (!res.ok) throw new Error(data.error?.message || "Failed to upload image");
			
			setUserData(data.data.user);
			
			toast({
				title: "Image Uploaded",
				description: "Your profile image has been updated."
			});
		} catch (error) {
			toast({
				title: 'Upload Failed',
				description: error instanceof Error ? error.message : 'Failed to upload image',
				variant: 'destructive'
			})
		} finally {
			setIsUploadingImage(false)
		}
	}

	// Handle account deletion
	const handleDeleteAccount = async () => {
		// Validate password is entered
		if (!deletePassword.trim()) {
			setDeletePasswordError('Please enter your password to confirm')
			return
		}

		setDeletePasswordError('')
		setIsDeleting(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'DELETE',
				body: JSON.stringify({ password: deletePassword }),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to delete account')
			}

			// Clear session and sign out
			document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
			await signOut(auth)
			setUserData(null)

			if (isConnected) {
				await disconnectWallet()
			}

			toast({
				title: 'Account Deleted',
				description: 'Your account has been successfully deleted.'
			})

			navigate('/login')
		} catch (error) {
			toast({
				title: 'Deletion Failed',
				description: error instanceof Error ? error.message : 'Failed to delete account',
				variant: 'destructive'
			})
		} finally {
			setIsDeleting(false)
			setShowDeleteModal(false)
			setDeletePassword('')
		}
	}

	const handleLogOut = async () => {
		try {
			setIsLoggingOut(true)
			// Reset cookie to prevent re-login after sign out
			document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
			await signOut(auth)
	
			setUserData(null)
			if (isConnected) {
				await disconnectWallet()
			}
			navigate('/login')
		}
		finally {
			setIsLoggingOut(false)
		}
	}

	const togglePassword = (key: 'current' | 'new' | 'confirm') => {
		setShowPassword((prev) => ({
		  ...prev,
		  [key]: !prev[key],
		}))
	}
	  

	const closeModal = (type: 'email' | 'password' | 'delete' | 'logout') => {
		if (type === 'email') {
		  setShowEmailModal(false)
		  emailForm.reset({ email: email || '' })
		}
	  
		if (type === 'password') {
		  setShowPasswordModal(false)
		  passwordForm.reset()
		  setShowPassword({ current: false, new: false, confirm: false })
		}
	  
		if (type === 'delete') {
		  setShowDeleteModal(false)
		  setDeletePassword('')
		  setDeletePasswordError('')
		}

		if (type === 'logout') {
			setShowConfirmModal(false)
		}
	}
	  
	// Get user initials for avatar
	const userInitials = displayName
		? displayName
				.split(' ')
				.map((name: string) => name[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: email?.slice(0, 2).toUpperCase() || 'U'

	
		return (
			<div className="min-h-auto bg-[#18181B] text-white">
			  <div className="max-w-5xl mx-auto px-5 pb-8">
				{/* Header with back button */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 mb-[12px]"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">Account</h1>
				</div>

				{/* PROFILE CARD */}
				<div className="bg-[#212124] rounded-3xl py-8 px-4 flex flex-col items-center w-full">
				  {/* Avatar */}
				  <div className='flex items-center flex-col mb-6'>
				  	<div className="relative mb-4">
						<div className="absolute inset-0 rounded-full bg-[#EF4444] blur-lg opacity-40" />

						{profileImageUrl ? (
							<img src={profileImageUrl} alt="Profile" className="relative h-24 w-24 rounded-full object-cover border-2 border-[#EF4444]" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }} />
						) : (
							<div className="relative h-24 w-24 rounded-full bg-[#2F2F32] border-2 border-[#EF4444] flex items-center justify-center text-2xl font-semibold" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }} >
								{userInitials}
							</div>
						)}

						{/* Edit Icon */}
						<div className="absolute bottom-1 right-1 h-[24px] w-[24px] rounded-full bg-[#FFFFFF] flex items-center justify-center cursor-pointer" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}>
							{isUploadingImage ? (
								<Loader2 className="h-4 w-4 animate-spin text-[#EF4444]" />
							) : (
								<PencilIcon className="h-4 w-4" fill="#EF4444" stroke="#FFFFFF" strokeWidth={1}/>
							)}
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							hidden
							onChange={(e) => {
							const file = e.target.files?.[0];
							if (!file) return;
							cropperRef.current?.openWithFile(file);
							e.target.value = "";
							}}
						/>

						<ImageCropper
							ref={cropperRef}
							initialImage={profileImageUrl || undefined}
							initialFileName={getFileNameFromUrl(
								profileImageUrl
							)}
							aspectRatio={1}
							isPreviewVisible={false} 
							onSave={async ({ croppedUrl, fileName }) => {
								const blob = await (await fetch(croppedUrl)).blob();
								const file = new File([blob], fileName, { type: blob.type });
								handleImageUpload(file);
							}}
						/>
					</div>
			
					{/* Name + Email */}
					<h2 className="font-montserrat text-xl leading-[18px] font-bold mb-1">
						Hi {displayName || 'User'}!
					</h2>
					<p className="font-montserrat leading-[18px] text-sm text-[#FFFFFF]">{email}</p>
				  </div>

		  
					{/* ACTION LIST */}
					<div className="space-y-2 w-full">
					{accountActionItems?.map((item) => (
						<button
						key={item.label}
						onClick={item.onClick}
						className="w-full h-[42px] bg-[#2F2F32] rounded-lg px-4 py-4 flex items-center justify-between">
						<div className="flex items-center gap-3">
							<item.icon className="h-[20px] w-[20px] text-white" />
							<span className="font-figtree leading-[16x] text-sm font-regular">{item.label}</span>
						</div>
						<ChevronRight className="h-[20px] w-[20px] text-[#FFFFFF]" />
						</button>
					))}
					</div>
				</div>
			  </div>
				{(showEmailModal || showPasswordModal || showDeleteModal || showConfirmModal) && (
					<>	
						{showEmailModal && (
							<BottomSheet
								open={showEmailModal}
								onClose={() => closeModal('email')}
								title="Change Email"
								footer={
									<Button
									onClick={emailForm.handleSubmit(onEmailSubmit)}
									disabled={isUpdatingEmail}
									className="w-full h-[42px] bg-[#EF4444] rounded-[26px] text-white font-semibold font-figtree shadow-[0_6px_20px_rgba(239,68,68,0.45)]	active:scale-[0.96]">{isUpdatingEmail ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Updating...
										</>
									) : (
										'Update Email'
									)}</Button>
								}
								>
								<div className="space-y-2">
									<label className="font-figtree font-medium leading-[16px] text-[#F3F3F3] text-sm">Email</label>

									<input
									autoFocus
									type="email"
									placeholder="Enter Email"
									{...emailForm.register('email')}
									className="w-full h-[42px]	bg-[#212124] text-xs leading-4 font-normal rounded-[8px]	px-3 placeholder:font-figtree placeholder:font-normal placeholder:leading-[16px] placeholder:text-xs placeholder:text-[#AFAFAF] text-white outline-none"
									/>
									{emailForm.formState.errors.email && (
									<p className="text-sm text-[#EF4444] font-figtree font-normal leading-[16px]">
										{emailForm.formState.errors.email.message}
									</p>
									)}
								</div>
							</BottomSheet>
						)}
						{showPasswordModal && (

							<BottomSheet
								open={showPasswordModal}
								onClose={() => closeModal('password')}
								title="Change Password"
								footer={
									<Button
									onClick={passwordForm.handleSubmit(onPasswordSubmit)}
									disabled={isUpdatingPassword}
									className="w-full h-[42px] bg-[#EF4444] font-figtree font-semibold leading-[14px] rounded-[26px] text-white text-sm shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]">{isUpdatingPassword ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Updating...
										</>
									) : (
										'Change Password'
									)}</Button>
								}
								>
								{/* New Password */}
								<div className="space-y-2">
									<label className="font-figtree font-medium leading-[16px] text-[#F3F3F3] text-[13px]">New Password</label>
									<div className='relative'>
									<input
									autoFocus
									type={showPassword.new ? 'text' : 'password'}
									placeholder="Enter New Password"
									{...passwordForm.register('newPassword')}
									className="w-full h-[42px]	bg-[#212124] text-xs leading-4 font-normal rounded-[8px]	px-3 placeholder:font-figtree placeholder:font-normal placeholder:leading-[16px] placeholder:text-xs placeholder:text-[#AFAFAF] text-white outline-none relative"
									/>
									<button
									type="button"
									onClick={() => togglePassword('new')}
									className='absolute right-3 top-1/2 -translate-y-1/2'
									>
									{showPassword.new ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
									</button>
									</div>
									{passwordForm.formState.errors.newPassword && (
									<p className="text-sm text-[#EF4444] font-figtree font-normal leading-[16px]">
										{passwordForm.formState.errors.newPassword.message}
									</p>
									)}
								</div>

								{/* Confirm Password */}
								<div className="space-y-2">
									<label className="font-figtree font-medium leading-[16px] text-[#F3F3F3] text-[13px]">Confirm Password</label>
									<div className='relative'>
									<input
									type={showPassword.confirm ? 'text' : 'password'}
									placeholder="Enter Confirm Password"
									{...passwordForm.register('confirmPassword')}
									className="w-full h-[42px]	bg-[#212124] rounded-[8px]	px-3 text-xs leading-4 font-regular	placeholder:font-figtree placeholder:font-normal placeholder:leading-[16px] placeholder:text-xs placeholder:text-[#AFAFAF] text-white outline-none relative"
									/>
									<button
									type="button"
									onClick={() => togglePassword('confirm')}
									className='absolute right-3 top-1/2 -translate-y-1/2'
									>
									{showPassword.confirm ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
									</button>
									</div>
									{passwordForm.formState.errors.confirmPassword && (
									<p className="text-sm text-[#EF4444] font-figtree font-normal leading-[16px]">
										{passwordForm.formState.errors.confirmPassword.message}
									</p>
									)}
								</div>
							</BottomSheet>
						)}
						{showDeleteModal && (
							<BottomSheet
								open={showDeleteModal}
								onClose={() => closeModal('delete')}
								title="Delete Account"
								footer={
									<>
									<AlertDialogCancel
										onClick={() => closeModal('delete')}
										className="w-full h-[42px] mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B]"
									>
										Cancel
									</AlertDialogCancel>
									<Button
									onClick={(e) => {
										e.preventDefault()
										handleDeleteAccount()
									}}
									disabled={isDeleting}
									className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold	rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)]	active:scale-[0.96]">{isDeleting ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Deleting...
										</>
									) : (
										'Delete Account'
									)}</Button>
									</>
								}
								>
								<h1 className='font-figtree font-semibold text-[18px] leading-[22px] text-[#ffffff] mb-[-16px]'>Are you sure you want to delete your account?</h1>
								{/* Description */}
								<p className="font-figtree font-normal leading-[18px] text-[13px] text-[#BABABB]">
									This action cannot be undone. Your account will be deactivated and your data will be removed.
								</p>

								{/* Password Input */}
								<div className="space-y-1">
									<label className="font-figtree font-medium leading-4 text-[#F3F3F3] text-[13px]"> Password </label>

									<input autoFocus type="password" placeholder="Enter your password to confirm" value={deletePassword}
									onChange={(e) => {
										setDeletePassword(e.target.value)
										setDeletePasswordError('')
									}}
									className="w-full h-[42px]	bg-[#212124] rounded-[8px]	px-3	placeholder:font-figtree placeholder:font-normal placeholder:leading-[16px] placeholder:text-xs placeholder:text-[#AFAFAF] text-[#AFAFAF] outline-none"/>

									{/* Error Message */}
									{deletePasswordError && (
									<p className="text-sm text-[#EF4444] mt-1"> {deletePasswordError}</p>
									)}
								</div>
							</BottomSheet>
						)}
						{showConfirmModal && (
							<BottomSheet
								open={showConfirmModal}
								onClose={() => closeModal('logout')}
								title="Sign Out"
								footer={
								<>
									<AlertDialogCancel
									onClick={() => closeModal('logout')}
									className="w-full h-[42px] mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B]"
									>
									Cancel
									</AlertDialogCancel>

									<Button
									onClick={(e) => {
										e.preventDefault()
										handleLogOut()
									}}
									disabled={isLoggingOut}
									className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
									>
									{isLoggingOut ? (
										<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Signing out...
										</>
									) : (
										'Sign Out'
									)}
									</Button>
								</>
								}
							>
								<h1 className="font-figtree font-semibold text-[18px] leading-[22px] text-[#ffffff] mb-[-16px]">
								Are you sure you want to Sign Out?
								</h1>

								<p className="font-figtree font-regular leading-[18px] text-[13px] text-[#BABABB]">
								You will be logged out from your current session. You can log back in anytime.
								</p>
							</BottomSheet>
						)}
					</>
				)}
			</div>
		)  
}
